const MARKETING_OPT_IN_ENDPOINT = '/contact#stz-quiz-marketing';
const SAVED_CHARTS_KEY = 'stz_saved_charts';
// The retired Fable teaser used stz_birth_quiz_dismissed_until, stz_quiz_invite_shown and
// stz_quiz_invite_manual_suppressed. Those keys belong to a different UI and must never suppress
// the consolidated Find Your Sky quiz. This versioned contract records only actions on the one real
// quiz modal: an intentional close, a successful automatic open, or a manual launcher click.
// The existing 14-day dismissal window governs both the modal and its compact launcher.
const QUIZ_STORAGE_KEYS = Object.freeze({
  dismissedUntil: 'stz_find_your_sky_dismissed_until_v1',
  autoOpened: 'stz_find_your_sky_auto_opened_v1',
  manualSuppressed: 'stz_find_your_sky_manual_suppressed_v1',
});
const DISMISSED_UNTIL_KEY = QUIZ_STORAGE_KEYS.dismissedUntil;
// The sticky launcher and the delayed entry point open this same quiz overlay. Nothing appears in
// the first 10s; after that, ~35% scroll or the 12.5s backstop opens it once. Product pages keep
// their own purchase-intent flow.
const AUTO_OPEN_MIN_DELAY_MS = 10000;
const AUTO_OPEN_MAX_DELAY_MS = 12500;
const AUTO_OPEN_SCROLL_RATIO = 0.35;
// A long homepage makes 35% a huge distance. After the 10s quiet period, one meaningful
// viewport-ish scroll must also count so incognito users are not stuck waiting for the 12.5s backstop.
const AUTO_OPEN_SCROLL_PX = 480;
const AUTO_OPEN_SESSION_KEY = QUIZ_STORAGE_KEYS.autoOpened;
const AUTO_OPEN_MANUAL_SUPPRESS_KEY = QUIZ_STORAGE_KEYS.manualSuppressed;
const MOBILE_LAUNCHER_MEDIA_QUERY = '(max-width: 749px)';
// The launcher must yield only to controls a visitor can actually activate. Passive copy is never
// part of this contract, and quiz controls are excluded because they belong to the same UI root.
const QUIZ_ACTIONABLE_CONTROL_SELECTOR = [
  'a[href]',
  'button:not([disabled]):not([aria-disabled="true"])',
  'input:not([disabled]):not([aria-disabled="true"])',
  'select:not([disabled]):not([aria-disabled="true"])',
  'textarea:not([disabled]):not([aria-disabled="true"])',
  '[role="button"]:not([aria-disabled="true"])',
  '[role="link"]:not([aria-disabled="true"])',
].join(', ');

// Shopify owns the customer/newsletter submission. A short backstop covers a genuinely interrupted
// storefront request; there is no separate app service to wake up.
const QUIZ_REASSURE_AFTER_MS = 5000;
const QUIZ_SUBMIT_TIMEOUT_MS = 20000;
const DISMISS_DAYS = 14;
const QUIZ_PREVIEW_ENDPOINT = '/apps/chart-preview';
const FIXED_ART_BUNDLE_HANDLES = new Set([
  'two-tees',
  'sun-moon',
  'crystal-veil-moon',
  'black-halo-moon',
  'dead-stars-afterlight',
]);

// SAVED-CHART CONTRACT -- founder decision 2026-08-22 (.team/decisions.md).
//
// Keeping a birth chart is its own decision, not a side effect of joining the mailing list, so it
// has its own unchecked box and its own copy. Every stored chart dies 30 days after it was saved,
// the customer can delete one or all of them, and no storage failure anywhere in here may stop the
// quiz, personalization, the cart or checkout: every path returns a value instead of throwing.
const SAVED_CHART_SCHEMA = 1;
const SAVED_CHART_TTL_MS = 30 * 86400000;
// Bounds, because this key is not ours alone. It was written by an older build that validated
// nothing, and anything running on the origin can put anything in it. Each read is capped before it
// costs work: bytes before we parse, items before we validate, characters before we render.
const SAVED_CHART_MAX_BYTES = 20000;
const SAVED_CHART_MAX_ITEMS = 12;
const SAVED_CHART_MAX_FIELD_CHARS = 120;
const SAVED_CHART_MAX_TZ_CHARS = 64;
// The quiz date input is already bounded `min="1900-01-01" max="<today>"`; storage enforces the same
// window, so a record cannot hold a birth date the form would never have produced.
const SAVED_CHART_MIN_DATE = '1900-01-01';
// The complete set of keys a saved chart may carry. An unknown key makes the whole record invalid
// rather than being quietly stripped, so a capability, a contact-verification flag, a raw server
// response, an email or any other unrelated customer state cannot ride along by
// being ignored. Fail closed: we drop the record, we do not clean it up and keep it.
const SAVED_CHART_KEYS = new Set([
  'v', 'id', 'label', 'isSelf', 'date', 'time', 'place', 'lat', 'lon', 'tz', 'savedAt', 'expiresAt',
  'timeNote', 'timeUnknown',
]);
const PHOTON_ENDPOINT = 'https://photon.komoot.io/api/';

const SIGN_LINES = {
  Aries: 'First through the door. You move before the world is ready, and it follows.',
  Taurus: 'Built to last. You make beauty something you can touch.',
  Gemini: 'Two minds, one current. You are the conversation everyone remembers.',
  Cancer: 'The tide remembers everything. You feel in a key the rest of us forgot.',
  Leo: 'Born lit. A room reorganizes itself around you and pretends it always did.',
  Virgo: 'You see the seam in everything and quietly make it perfect.',
  Libra: 'You hold the scales the rest of us drop. Grace as a discipline.',
  Scorpio: 'Still water, deep current. People feel you before they see you.',
  Sagittarius: 'An arrow already loosed. You were never meant to stay.',
  Capricorn: 'You climb in the dark and reach the summit on schedule.',
  Aquarius: 'You live a few years ahead and send the rest of us postcards.',
  Pisces: 'You dream in a language we are still learning to read.',
};

const FIELD_SELECTOR = {
  date: '[name="properties[_Birth Date]"], [name="properties[Birth Date]"]',
  time: '[name="properties[_Birth Time]"], [name="properties[Birth Time]"]',
  place: '[data-place-input], [name="properties[Birth Place]"]',
  lat: '[name="properties[_Birth Lat]"]',
  lon: '[name="properties[_Birth Lng]"]',
  timeNote: '[name="properties[Birth Time Note]"]',
};

// Guarded so the file can be loaded outside a browser. The storage contract below is the privacy
// boundary and has to be provable without a DOM; scripts/tests/saved-chart-privacy.test.mjs loads
// this exact asset, with no document, and drives the seam exported at the bottom.
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStarlyzedChartQuiz, { once: true });
  } else {
    initStarlyzedChartQuiz();
  }
}

function initStarlyzedChartQuiz() {
  mountQuiz();
  prefillPersonalizedProduct();
  // Saved-charts picker DISABLED (2026-07-01, Jade's call). The "Saved charts" widget front-loaded a
  // power-user CRM (chart dropdown, "save for next time", "this is my chart", a dead "add another
  // person" button) above the birth-data form on one-time-buy products; ~95% of buyers are first-timers
  // who never use it. The clean birth form + DOB gate + age-fit card stand on their own. The one real
  // value (silent cross-product prefill) can return later as a no-UI one-liner — the function below is
  // kept intact for that.
  // mountProductChartPicker();
}

function mountQuiz() {
  if (document.querySelector('[data-stz-quiz-root]')) return;
  if (location.pathname.startsWith('/cart') || location.pathname.startsWith('/checkout')) return;

  const root = document.createElement('div');
  root.className = 'stz-quiz';
  root.dataset.stzQuizRoot = 'true';
  root.innerHTML = `
    <div class="stz-quiz__launcher-group" data-quiz-launcher>
      <button class="stz-quiz__launcher" type="button" data-quiz-open aria-label="Open Find your sky quiz">
        <span aria-hidden="true">✦</span>
        <span>Find your sky</span>
      </button>
      <button class="stz-quiz__launcher-dismiss" type="button" data-quiz-launcher-dismiss aria-label="Dismiss Find your sky launcher">
        <span aria-hidden="true">×</span>
      </button>
    </div>
    <div class="stz-quiz__overlay" data-quiz-overlay hidden>
      <section class="stz-quiz__dialog" role="dialog" aria-modal="true" aria-labelledby="stz-quiz-title">
        <button class="stz-quiz__close" type="button" aria-label="Close" data-quiz-close>×</button>
        <form class="stz-quiz__form" data-quiz-form novalidate>
          <div class="stz-quiz__step" data-step="intro">
            <p class="stz-quiz__eyebrow">Starlyzed chart ritual</p>
            <h2 id="stz-quiz-title">Find your sky</h2>
            <p>Answer three things about the moment you arrived. We will read your chart and render it as something you can wear.</p>
            <button class="stz-quiz__button" type="button" data-next="date">Begin</button>
          </div>
          <div class="stz-quiz__step" data-step="date" hidden>
            <p class="stz-quiz__eyebrow">01 / Date</p>
            <h2>What’s your birthday?</h2>
            <label for="stz-quiz-date">Date of birth<input id="stz-quiz-date" name="birth_date" class="stz-quiz__input" type="date" min="1900-01-01" max="${today()}" data-quiz-date required></label>
            <p class="stz-quiz__error" data-error="date" hidden></p>
            <button class="stz-quiz__button" type="button" data-next="time">Next</button>
          </div>
          <div class="stz-quiz__step" data-step="time" hidden>
            <p class="stz-quiz__eyebrow">02 / Time</p>
            <h2>What time?</h2>
            <p>Your birth time unlocks your rising sign and your houses. If you do not know it, that is okay.</p>
            <label for="stz-quiz-time">Birth time<input id="stz-quiz-time" name="birth_time" class="stz-quiz__input" type="time" data-quiz-time></label>
            <button class="stz-quiz__link" type="button" data-skip-time>I do not know my time</button>
            <button class="stz-quiz__button" type="button" data-next="place">Next</button>
          </div>
          <div class="stz-quiz__step" data-step="place" hidden>
            <p class="stz-quiz__eyebrow">03 / Place</p>
            <h2>Where were you born?</h2>
            <p>Your birthplace places the moon and the planets exactly.</p>
            <label for="stz-quiz-place">City of birth
              <input id="stz-quiz-place" name="birth_place" class="stz-quiz__input" type="text" autocomplete="off" spellcheck="false" data-quiz-place placeholder="City, region, country">
            </label>
            <ul class="stz-quiz__suggestions" data-quiz-suggestions hidden></ul>
            <button class="stz-quiz__button" type="button" data-next="reveal">Reveal my sign</button>
          </div>
          <div class="stz-quiz__step" data-step="reveal" hidden>
            <p class="stz-quiz__eyebrow">Your surface sign</p>
            <h2 data-reveal-title>You are a sun.</h2>
            <p data-reveal-line></p>
            <button class="stz-quiz__button" type="button" data-next="chart">Reveal my chart</button>
          </div>
          <div class="stz-quiz__step" data-step="chart" hidden>
            <div class="stz-quiz__preview" data-quiz-preview>
              <p class="stz-quiz__small" data-quiz-preview-status role="status" aria-live="polite">Complete your birth time and choose your birthplace to see your wheel.</p>
              <div class="stz-quiz__preview-svg" data-quiz-preview-svg aria-live="polite"></div>
            </div>
            <button class="stz-quiz__button" type="button" data-next="email">Continue to your offer</button>
            <div class="stz-quiz__nav">
              <button class="stz-quiz__link" type="button" data-back="time">Back</button>
            </div>
          </div>
          <div class="stz-quiz__step" data-step="email" hidden>
            <p class="stz-quiz__eyebrow">Your offer</p>
            <h2>Stay in the loop?</h2>
            <p>Enter your email for 10% off your first piece and early access to new drops.</p>
            <label for="stz-quiz-first-name">First name<input id="stz-quiz-first-name" name="first_name" class="stz-quiz__input" type="text" autocomplete="given-name" data-quiz-first-name></label>
            <label for="stz-quiz-email">Email address<input id="stz-quiz-email" name="email" class="stz-quiz__input" type="email" autocomplete="email" data-quiz-email required></label>
            <p class="stz-quiz__consent">Unsubscribe anytime. We never share your details.</p>
            <label class="stz-quiz__checkbox">
              <input type="checkbox" data-quiz-marketing-opt-in>
              <span>Email me 10% off my first piece and early access to new drops.</span>
            </label>
            <label class="stz-quiz__checkbox">
              <input type="checkbox" data-quiz-save-chart>
              <span>Also keep my birth details in this browser for 30 days so I do not have to type them again. They stay on this device, and you can delete them here anytime.</span>
            </label>
            <p class="stz-quiz__error" data-error="email" hidden></p>
            <p class="stz-quiz__error" data-error="form" hidden></p>
            <p class="stz-quiz__small" data-status role="status" aria-live="polite" hidden></p>
            <p class="stz-quiz__small" data-quiz-outcome role="status" aria-live="polite" hidden></p>
            <button class="stz-quiz__button" type="submit" data-submit>Unlock 10%</button>
          </div>
          <div class="stz-quiz__step" data-step="success" hidden>
            <p class="stz-quiz__eyebrow">You are in</p>
            <h2 data-success-title>You’re all set.</h2>
            <p data-success-body></p>
            <p class="stz-quiz__small" data-preview-note></p>
            <p class="stz-quiz__small" data-save-note></p>
            <p class="stz-quiz__small" data-marketing-note></p>
            <a class="stz-quiz__button stz-quiz__button--link" href="/collections/personalized-astrology-gifts">Shop your chart</a>
          </div>
        </form>
        <section class="stz-quiz__saved" data-saved-charts hidden>
          <p class="stz-quiz__eyebrow">Saved on this device</p>
          <p class="stz-quiz__small" data-saved-note role="status" aria-live="polite" hidden></p>
          <ul class="stz-quiz__saved-list" data-saved-list></ul>
          <button class="stz-quiz__link" type="button" data-saved-clear>Delete all saved charts</button>
        </section>
      </section>
    </div>
  `;

  document.body.appendChild(root);
  const state = { step: 'intro', date: '', time: '', place: '', lat: '', lon: '', sign: '', timeUnknown: false, timeNote: '' };
  const revealController = createQuizRevealController({
    renderPreview: () => renderQuizPreview(root, state),
  });
  const overlay = root.querySelector('[data-quiz-overlay]');
  const launcherGroup = root.querySelector('[data-quiz-launcher]');
  const launcher = root.querySelector('[data-quiz-open]');
  const launcherDismiss = root.querySelector('[data-quiz-launcher-dismiss]');
  const close = root.querySelector('[data-quiz-close]');
  if (quizDismissedUntil() > Date.now()) launcherGroup.hidden = true;
  const form = root.querySelector('[data-quiz-form]');
  const dateInput = root.querySelector('[data-quiz-date]');
  const timeInput = root.querySelector('[data-quiz-time]');
  const placeInput = root.querySelector('[data-quiz-place]');
  const suggestions = root.querySelector('[data-quiz-suggestions]');

  launcher?.addEventListener('click', () => openQuiz(root));
  close?.addEventListener('click', () => closeQuiz(root, true));
  overlay?.addEventListener('click', (event) => {
    if (event.target === overlay) closeQuiz(root, true);
  });
  root.querySelector('[data-skip-time]')?.addEventListener('click', () => {
    if (timeInput) timeInput.value = '';
    state.time = '';
    state.timeUnknown = true;
    showStep(root, 'place');
  });
  // The chart step used to be a dead end: skip the time, reach "Add your birth time to see your
  // wheel", and the only way to add it was to redo the quiz (founder, 2026-09-20). data-back
  // returns to an earlier step with every typed value still in the form.
  root.addEventListener('click', (event) => {
    const back = event.target instanceof Element ? event.target.closest('[data-back]') : null;
    if (!back) return;
    showStep(root, back.getAttribute('data-back') || 'time');
    root.querySelector('[data-quiz-' + (back.getAttribute('data-back') || 'time') + ']')?.focus();
  });
  root.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-next]') : null;
    if (!button) return;
    const next = button.getAttribute('data-next') || 'intro';
    if (next === 'time' && !dateInput.value) {
      showError(root, 'date', 'Add your birth date to reveal your sign.');
      return;
    }
    if (next === 'reveal') {
      state.date = dateInput.value;
      state.time = timeInput.value;
      if (state.time) state.timeUnknown = false;
      state.place = placeInput.value.trim();
      state.sign = sunSign(state.date);
      renderReveal(root, state.sign);
      state.previewStatus = 'not_rendered';
      revealController.enterSign();
    }
    if (next === 'chart' && !revealController.revealChart({ userInitiated: true })) {
      return;
    }
    showStep(root, next);
  });
  form?.addEventListener('submit', async (event) => {
    event.preventDefault();
    await submitQuiz(root, state);
  });
  placeInput?.addEventListener('input', debounce(() => searchPlace(placeInput, suggestions, state), 280));
  placeInput?.addEventListener('blur', () => setTimeout(() => closeSuggestions(suggestions), 150));

  // Deletion. Both controls are real buttons inside the dialog, so they are reachable by tab and
  // fire on Enter and Space with no key handling of our own. Deleting reports what actually
  // happened: a browser that refuses to write is told about rather than papered over with a
  // "deleted" message for data still sitting on the device.
  root.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const perChart = target.closest('[data-delete-chart]');
    if (perChart) {
      const removed = deleteSavedChart(perChart.getAttribute('data-delete-chart') || '');
      renderSavedCharts(root, removed
        ? 'Deleted from this browser.'
        : 'This browser would not let us delete it. Clearing site data for starlyzed.com removes it.');
      return;
    }
    if (target.closest('[data-saved-clear]')) {
      const cleared = clearSavedCharts();
      renderSavedCharts(root, cleared
        ? 'All saved charts deleted from this browser.'
        : 'This browser would not let us delete them. Clearing site data for starlyzed.com removes them.');
    }
  });
  renderSavedCharts(root);

  // The persistent launcher-dismissal control is usable even if optional delayed discovery cannot
  // initialize. Its closure receives a controller when one exists and otherwise dismisses safely.
  let autoOpenController = null;
  bindQuizLauncherDismissal(launcherDismiss, root, () => autoOpenController);
  try {
    autoOpenController = mountQuizAutoOpen(root);
  } catch {
    // Automatic discovery must never take down the launcher, quiz, cart, or checkout.
  }
  try {
    mountQuizLauncherCollisionSuppression(root);
  } catch {
    // Collision avoidance is progressive enhancement: failure leaves the established launcher intact.
  }
}

// The saved list is re-read (and therefore re-pruned) every time it is drawn, so an expired chart
// can never be shown, applied, or deleted from a stale copy.
function renderSavedCharts(root, note) {
  const panel = root.querySelector('[data-saved-charts]');
  const list = root.querySelector('[data-saved-list]');
  const summary = root.querySelector('[data-saved-note]');
  if (!panel || !list || !summary) return;
  const charts = savedCharts();
  // The note is an on-demand status region, not a subtitle. Founder decision 2026-08-24: the opt-in
  // already promises browser-only storage for 30 days and every row already counts down its own
  // days, so a third standing copy of the same sentence was repetition. What is left here is the
  // outcome of an action — a deletion that worked, or a browser that refused one — and when there is
  // no outcome to report the region takes up no room. Unhidden before the text is written so the
  // live region actually announces the change rather than mutating while hidden.
  summary.hidden = !note;
  summary.textContent = note || '';
  if (!charts.length) {
    list.innerHTML = '';
    panel.hidden = !note;
    return;
  }
  panel.hidden = false;
  list.innerHTML = charts.map((chart) => {
    const label = chart.label || 'Saved chart';
    return `
      <li class="stz-quiz__saved-item">
        <span>${escapeHtml(label)} · born ${escapeHtml(birthDateLabel(chart.date))} · ${escapeHtml(expiryLabel(chart.expiresAt))}</span>
        <button class="stz-quiz__saved-delete" type="button" data-delete-chart="${escapeHtml(chart.id)}" aria-label="Delete the saved chart for ${escapeHtml(label)}">Delete</button>
      </li>`;
  }).join('');
}

// The by-hand fallback for birthDateLabel, used only when the runtime cannot build a formatter.
const MONTH_NAMES_EN = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * The visible birthday only. What is stored stays the exact ISO day the customer typed, because
 * that is what the engine reads; this is the label beside it.
 *
 * `timeZone: 'UTC'` is the whole point of the helper. `Date.UTC(1999, 11, 31)` rendered in the
 * browser's own zone reads as December 30 anywhere west of Greenwich, which would show a customer
 * the wrong birthday. Passing no locale is deliberate too: undefined means the browser's locale,
 * so the same record reads as December 31, 1999 in en-US and 31 de diciembre de 1999 in es-ES.
 */
function birthDateLabel(iso) {
  if (typeof iso !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return typeof iso === 'string' ? iso : '';
  const [year, month, day] = iso.split('-').map(Number);
  if (month < 1 || month > 12 || day < 1 || day > 31) return iso;
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(Date.UTC(year, month - 1, day)));
  } catch (error) {
    // A locale the runtime cannot build is not a reason to show the customer a raw ISO string —
    // that is the display Jade rejected. Fail open to the same long English shape by hand.
    return `${MONTH_NAMES_EN[month - 1]} ${day}, ${year}`;
  }
}

function expiryLabel(expiresAt) {
  const days = Math.max(1, Math.ceil((expiresAt - Date.now()) / 86400000));
  return days === 1 ? 'deletes tomorrow' : `deletes in ${days} days`;
}

function mountProductChartPicker() {
  if (!document.querySelector(FIELD_SELECTOR.date)) return;
  if (document.querySelector('[data-stz-chart-picker]')) return;

  const firstField = document.querySelector('product-custom-property-component');
  if (!firstField) return;

  const picker = document.createElement('section');
  picker.className = 'stz-chart-picker';
  picker.dataset.stzChartPicker = 'true';
  picker.innerHTML = `
    <div class="stz-chart-picker__head">
      <p class="stz-chart-picker__eyebrow">Saved charts</p>
      <p data-picker-summary></p>
    </div>
    <div class="stz-chart-picker__controls" data-picker-saved hidden>
      <label>Use a saved chart
        <select class="stz-chart-picker__select" data-picker-select></select>
      </label>
      <button class="stz-chart-picker__button" type="button" data-picker-apply>Use this chart</button>
      <button class="stz-chart-picker__link" type="button" data-picker-new>Add another person's chart</button>
    </div>
    <div class="stz-chart-picker__save">
      <label class="stz-chart-picker__checkbox">
        <input type="checkbox" data-picker-save>
        <span>Save these birth details for next time</span>
      </label>
      <input class="stz-chart-picker__label" type="text" data-picker-label placeholder="Chart label, e.g. Mom" hidden>
      <label class="stz-chart-picker__checkbox" data-picker-self-row hidden>
        <input type="checkbox" data-picker-self>
        <span>This is my chart</span>
      </label>
    </div>
  `;
  firstField.parentNode?.insertBefore(picker, firstField);

  const select = picker.querySelector('[data-picker-select]');
  const savedWrap = picker.querySelector('[data-picker-saved]');
  const summary = picker.querySelector('[data-picker-summary]');
  const saveToggle = picker.querySelector('[data-picker-save]');
  const saveLabel = picker.querySelector('[data-picker-label]');
  const selfRow = picker.querySelector('[data-picker-self-row]');
  const selfToggle = picker.querySelector('[data-picker-self]');
  const charts = savedCharts();

  if (charts.length) {
    savedWrap.hidden = false;
    summary.textContent = `${charts.length} saved ${charts.length === 1 ? 'chart' : 'charts'} on this device.`;
    select.innerHTML = charts.map((chart) => `<option value="${escapeHtml(chart.id)}">${escapeHtml(chart.label || 'Saved chart')}</option>`).join('');
    picker.querySelector('[data-picker-apply]')?.addEventListener('click', () => applySelectedChart(select.value));
    picker.querySelector('[data-picker-new]')?.addEventListener('click', () => {
      clearBirthFields();
      saveToggle.checked = true;
      saveLabel.hidden = false;
      selfRow.hidden = false;
      saveLabel.focus();
    });
    applyChart(charts[0]);
  } else {
    summary.textContent = 'Fill this once. Save it here for your next personalized piece.';
  }

  saveToggle?.addEventListener('change', () => {
    saveLabel.hidden = !saveToggle.checked;
    selfRow.hidden = !saveToggle.checked;
    if (saveToggle.checked && !saveLabel.value) saveLabel.value = 'Me';
  });

  const form = document.querySelector('form[data-type="add-to-cart-form"]');
  form?.addEventListener('submit', () => {
    if (!saveToggle?.checked) return;
    const chart = readProductBirthChart(saveLabel?.value || 'Me', Boolean(selfToggle?.checked));
    if (chart.date) saveChart(chart);
  }, true);

  function applySelectedChart(id) {
    const chart = savedCharts().find((item) => item.id === id);
    if (chart) applyChart(chart);
  }
}

function quizAutoOpenEligible({
  pathname = '',
  returningCustomer = false,
  hasSavedCharts = false,
  dismissedUntil = 0,
  now = Date.now(),
  sessionShown = false,
  manualSuppressed = false,
  hidden = false,
  quizOpen = false,
} = {}) {
  const route = String(pathname || '');
  return !route.includes('/products/')
    && !route.startsWith('/cart')
    && !route.startsWith('/checkout')
    && returningCustomer !== true
    && hasSavedCharts !== true
    && now > dismissedUntil
    && sessionShown !== true
    && manualSuppressed !== true
    && hidden !== true
    && quizOpen !== true;
}

function quizAutoOpenScrollReached(ratio, y = 0, {
  scrollRatio = AUTO_OPEN_SCROLL_RATIO,
  scrollPx = AUTO_OPEN_SCROLL_PX,
} = {}) {
  const safeRatio = Number(ratio);
  const safeY = Number(y);
  if (Number.isFinite(safeRatio) && safeRatio >= scrollRatio) return true;
  return Number.isFinite(safeY) && safeY >= scrollPx;
}

function quizAutoOpenScrollMetrics(event) {
  const doc = document.documentElement;
  const body = document.body;
  const viewport = window.innerHeight || 0;
  let y = Number(window.scrollY || doc?.scrollTop || body?.scrollTop || 0);
  let scrollable = Math.max(
    (doc?.scrollHeight || 0) - viewport,
    (body?.scrollHeight || 0) - viewport,
    0,
  );
  const target = event?.target;
  if (target && target !== document && target !== doc && target !== body) {
    const elScrollable = (target.scrollHeight || 0) - (target.clientHeight || 0);
    if (elScrollable > 1) {
      y = Math.max(y, Number(target.scrollTop || 0));
      scrollable = Math.max(scrollable, elScrollable);
    }
  }
  const ratio = scrollable <= 0 ? 1 : y / scrollable;
  return { y, ratio, viewport, scrollable };
}

function createQuizAutoOpenController({
  isEligible = () => true,
  open = async () => false,
  sessionGet: readSession = sessionGet,
  sessionSet: writeSession = sessionSet,
  setTimeoutImpl = setTimeout,
  clearTimeoutImpl = clearTimeout,
  minDelay = AUTO_OPEN_MIN_DELAY_MS,
  maxDelay = AUTO_OPEN_MAX_DELAY_MS,
  scrollRatio = AUTO_OPEN_SCROLL_RATIO,
  scrollPx = AUTO_OPEN_SCROLL_PX,
  onOpened = () => {},
} = {}) {
  let started = false;
  let shown = false;
  let minElapsed = false;
  let maxElapsed = false;
  let scrollHit = false;
  let minTimer = null;
  let maxTimer = null;

  function eligible() {
    return !shown && !readSession(AUTO_OPEN_SESSION_KEY) && Boolean(isEligible());
  }

  async function showQuiz() {
    if (!eligible()) return false;
    shown = true;
    let opened = false;
    try {
      opened = Boolean(await open());
    } catch {
      opened = false;
    }
    if (!opened) {
      // A missing overlay or failed open never consumes the session. Keep shown false so the next
      // valid bounded trigger can retry the same quiz.
      shown = false;
      return false;
    }
    // The quiz is observably open at this point. Storage is intentionally after that boundary;
    // a failed attempt cannot suppress the only automatic entry the customer could have seen.
    try {
      writeSession(AUTO_OPEN_SESSION_KEY, '1');
    } catch {
      // In-memory `shown` still prevents duplicates during this page session.
    }
    try {
      onOpened();
    } catch {
      // A telemetry/focus cleanup hook cannot turn a visible quiz into a retry.
    }
    return true;
  }

  function tryShow() {
    if (!minElapsed || (!scrollHit && !maxElapsed)) return Promise.resolve(false);
    return showQuiz();
  }

  function start() {
    if (started || !eligible()) return false;
    started = true;
    minTimer = setTimeoutImpl(() => {
      minElapsed = true;
      void tryShow();
    }, minDelay);
    maxTimer = setTimeoutImpl(() => {
      maxElapsed = true;
      void tryShow();
    }, maxDelay);
    return true;
  }

  function onScroll(ratio, y = 0) {
    if (!quizAutoOpenScrollReached(ratio, y, { scrollRatio, scrollPx })) {
      return Promise.resolve(false);
    }
    scrollHit = true;
    return tryShow();
  }

  function suppress() {
    shown = true;
    if (minTimer !== null) clearTimeoutImpl(minTimer);
    if (maxTimer !== null) clearTimeoutImpl(maxTimer);
  }

  return {
    start,
    showQuiz,
    tryShow,
    onScroll,
    suppress,
    getState: () => ({ started, shown, minElapsed, maxElapsed, scrollHit }),
  };
}

function rectsOverlap(first, second) {
  return Boolean(first && second && first.left < second.right && first.right > second.left
    && first.top < second.bottom && first.bottom > second.top);
}

function visibleActionableControls(root) {
  return [...document.querySelectorAll(QUIZ_ACTIONABLE_CONTROL_SELECTOR)]
    .filter((control) => !root.contains(control))
    .map((control) => {
      const rect = control.getBoundingClientRect();
      const style = getComputedStyle(control);
      const visible = !control.hidden && rect.width > 0 && rect.height > 0
        && style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) !== 0;
      return { control, rect, visible };
    })
    .filter(({ visible }) => visible);
}

function launcherCollisionIntersections(root, launcherGroup) {
  const launcherRect = launcherGroup?.getBoundingClientRect();
  if (!launcherRect || launcherRect.width <= 0 || launcherRect.height <= 0) return [];
  return visibleActionableControls(root)
    .filter(({ rect }) => rectsOverlap(launcherRect, rect));
}

function createQuizLauncherCollisionController({
  launcherGroup,
  isMobile = () => typeof window !== 'undefined' && window.matchMedia(MOBILE_LAUNCHER_MEDIA_QUERY).matches,
  getIntersections = () => launcherCollisionIntersections(launcherGroup?.closest('[data-stz-quiz-root]'), launcherGroup),
  isLauncherFocused = () => Boolean(launcherGroup?.contains(document.activeElement)),
  requestFrame = (callback) => window.requestAnimationFrame(callback),
  cancelFrame = (frame) => window.cancelAnimationFrame(frame),
} = {}) {
  let suppressed = false;
  let frame = null;

  function setSuppressed(next) {
    if (!launcherGroup || suppressed === next) return;
    suppressed = next;
    if (next) launcherGroup.setAttribute('data-quiz-collision-suppressed', 'true');
    else launcherGroup.removeAttribute('data-quiz-collision-suppressed');
    if (next) {
      launcherGroup.setAttribute('aria-hidden', 'true');
      launcherGroup.setAttribute('inert', '');
    } else {
      launcherGroup.removeAttribute('aria-hidden');
      launcherGroup.removeAttribute('inert');
    }
  }

  function update() {
    frame = null;
    if (!launcherGroup || launcherGroup.hidden || !isMobile()) {
      setSuppressed(false);
      return false;
    }
    const collides = getIntersections().length > 0;
    // Do not erase active keyboard focus. focusout schedules the same check and applies the pending
    // suppression after the visitor leaves the launcher.
    if (collides && isLauncherFocused()) return false;
    setSuppressed(collides);
    return collides;
  }

  function schedule() {
    if (frame !== null) return;
    frame = requestFrame(update);
  }

  function destroy() {
    if (frame !== null) cancelFrame(frame);
    frame = null;
  }

  return { update, schedule, destroy, getState: () => ({ suppressed }) };
}

function mountQuizLauncherCollisionSuppression(root) {
  const launcherGroup = root.querySelector('[data-quiz-launcher]');
  if (!launcherGroup || typeof window === 'undefined' || !window.matchMedia) return null;
  const mobile = window.matchMedia(MOBILE_LAUNCHER_MEDIA_QUERY);
  const controller = createQuizLauncherCollisionController({
    launcherGroup,
    isMobile: () => mobile.matches,
  });
  let listening = false;

  const onViewportChange = () => controller.schedule();
  const onFocusOut = () => controller.schedule();
  const attach = () => {
    if (listening) return;
    listening = true;
    window.addEventListener('scroll', onViewportChange, { passive: true });
    window.addEventListener('resize', onViewportChange, { passive: true });
    window.visualViewport?.addEventListener('resize', onViewportChange, { passive: true });
    launcherGroup.addEventListener('focusout', onFocusOut);
  };
  const detach = () => {
    if (!listening) return;
    listening = false;
    window.removeEventListener('scroll', onViewportChange);
    window.removeEventListener('resize', onViewportChange);
    window.visualViewport?.removeEventListener('resize', onViewportChange);
    launcherGroup.removeEventListener('focusout', onFocusOut);
  };
  const onMediaChange = () => {
    if (mobile.matches) {
      attach();
      controller.update();
    } else {
      detach();
      controller.update();
    }
  };

  mobile.addEventListener?.('change', onMediaChange);
  if (mobile.matches) {
    attach();
    controller.update();
  }
  return {
    destroy() {
      detach();
      mobile.removeEventListener?.('change', onMediaChange);
      controller.destroy();
    },
  };
}

function hideQuizLauncher(root) {
  root?.querySelector('[data-quiz-launcher]')?.setAttribute('hidden', '');
}

function bindQuizLauncherDismissal(button, root, getAutoOpenController = () => null) {
  button?.addEventListener('click', () => dismissQuizLauncher(root, {
    autoOpenController: getAutoOpenController(),
  }));
}

function dismissQuizLauncher(root, { autoOpenController = null, remember = rememberQuizDismissal } = {}) {
  hideQuizLauncher(root);
  try {
    autoOpenController?.suppress();
  } catch {
    // Hiding the launcher must not depend on a delayed-entry controller being available.
  }
  sessionSet(AUTO_OPEN_MANUAL_SUPPRESS_KEY, '1');
  return remember(DISMISS_DAYS);
}

function mountQuizAutoOpen(root) {
  const launcher = root.querySelector('[data-quiz-open]');
  const overlay = root.querySelector('[data-quiz-overlay]');
  let onScroll;

  function quizIsOpen() {
    return Boolean(overlay && !overlay.hidden);
  }

  function autoOpenEligible() {
    const storageState = quizAutoOpenStorageState();
    return quizAutoOpenEligible({
      pathname: location.pathname,
      returningCustomer: document.body?.dataset?.stzReturningCustomer === 'true',
      hasSavedCharts: savedCharts().length > 0,
      dismissedUntil: storageState.dismissedUntil,
      now: Date.now(),
      sessionShown: storageState.sessionShown,
      manualSuppressed: storageState.manualSuppressed,
      hidden: document.hidden,
      quizOpen: quizIsOpen(),
    });
  }

  const controller = createQuizAutoOpenController({
    isEligible: autoOpenEligible,
    open: () => {
      openQuiz(root);
      return quizIsOpen();
    },
    onOpened: () => {
      if (onScroll) {
        window.removeEventListener('scroll', onScroll, { capture: true });
        document.removeEventListener('scroll', onScroll, { capture: true });
      }
    },
    minDelay: AUTO_OPEN_MIN_DELAY_MS,
    maxDelay: AUTO_OPEN_MAX_DELAY_MS,
    scrollRatio: AUTO_OPEN_SCROLL_RATIO,
    scrollPx: AUTO_OPEN_SCROLL_PX,
  });

  launcher?.addEventListener('click', () => {
    controller.suppress();
    sessionSet(AUTO_OPEN_MANUAL_SUPPRESS_KEY, '1');
  });

  onScroll = (event) => {
    const { ratio, y } = quizAutoOpenScrollMetrics(event);
    void controller.onScroll(ratio, y);
  };

  function arm() {
    if (!controller.start()) return false;
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    return true;
  }

  if (arm()) return controller;
  // A background tab is ineligible only while hidden. Retry once it is visible so a fresh
  // incognito preview is not permanently disarmed because it loaded in the background.
  if (document.hidden) {
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) arm();
    }, { once: true });
  }
  return controller;
}

function openQuiz(root) {
  const overlay = root.querySelector('[data-quiz-overlay]');
  if (!overlay) return;
  overlay.hidden = false;
  document.documentElement.classList.add('stz-quiz-open');
  renderSavedCharts(root);
  showStep(root, 'intro');
  root.querySelector('[data-quiz-close]')?.focus();
}

function closeQuiz(root, remember) {
  const overlay = root.querySelector('[data-quiz-overlay]');
  if (overlay) overlay.hidden = true;
  document.documentElement.classList.remove('stz-quiz-open');
  if (remember) {
    hideQuizLauncher(root);
    rememberQuizDismissal(DISMISS_DAYS);
  }
}

function showStep(root, step) {
  for (const node of root.querySelectorAll('[data-step]')) node.hidden = node.getAttribute('data-step') !== step;
}

function createQuizRevealController({ renderPreview = () => {} } = {}) {
  let phase = 'idle';

  function enterSign() {
    phase = 'sign';
    return phase;
  }

  function revealChart({ userInitiated = false } = {}) {
    if (!userInitiated || phase !== 'sign') return false;
    phase = 'chart';
    void renderPreview();
    return true;
  }

  return {
    enterSign,
    revealChart,
    getState: () => ({ phase }),
  };
}

async function submitQuiz(root, state) {
  const email = root.querySelector('[data-quiz-email]')?.value.trim();
  const firstName = root.querySelector('[data-quiz-first-name]')?.value.trim();
  const date = root.querySelector('[data-quiz-date]')?.value;
  const time = root.querySelector('[data-quiz-time]')?.value;
  const place = root.querySelector('[data-quiz-place]')?.value.trim();
  const wantsMarketingEmails = Boolean(root.querySelector('[data-quiz-marketing-opt-in]')?.checked);
  const wantsSavedChart = Boolean(root.querySelector('[data-quiz-save-chart]')?.checked);
  if (!email || !email.includes('@')) {
    showError(root, 'email', 'Add a valid email to continue.');
    return;
  }
  const button = root.querySelector('[data-submit]');
  if (button && button.disabled) return;
  if (button) button.disabled = true;

  hideNote(root, 'form');
  hideNote(root, 'email');

  const chart = {
    id: 'me',
    label: 'Me',
    isSelf: true,
    date,
    time: time || '',
    place: place || '',
    lat: state.lat || '',
    lon: state.lon || '',
    tz: '',
    ...optionalBirthTimeFields({
      time,
      timeUnknown: state.timeUnknown,
      timeNote: state.timeNote,
    }),
  };
  const saveOutcome = persistQuizChart(chart, wantsSavedChart);
  setQuizOutcome(root, { preview: state.previewStatus || 'not_rendered', save: saveOutcome, marketing: 'skipped' });

  if (!wantsMarketingEmails) {
    hideNote(root, 'status');
    renderSuccess(root, { optedIn: false, preview: state.previewStatus || 'not_rendered', save: saveOutcome });
    const saveNote = root.querySelector('[data-save-note]');
    if (saveNote) saveNote.textContent = savedChartNote(saveOutcome);
    renderSavedCharts(root);
    rememberQuizDismissal(365);
    if (button) button.disabled = false;
    return;
  }

  showStatus(root, 'Joining the list.');
  const reassure = setTimeout(() => showStatus(root, 'Still joining.'), QUIZ_REASSURE_AFTER_MS);
  const controller = new AbortController();
  const backstop = setTimeout(() => controller.abort(), QUIZ_SUBMIT_TIMEOUT_MS);

  try {
    await submitMarketingOptIn({
      marketingOptIn: true,
      email,
      firstName,
      sunSign: state.sign,
      date,
      signal: controller.signal,
    });
    hideNote(root, 'status');
    setQuizOutcome(root, { preview: state.previewStatus || 'not_rendered', save: saveOutcome, marketing: 'saved' });
    renderSuccess(root, { optedIn: true, preview: state.previewStatus || 'not_rendered', save: saveOutcome });
    const saveNote = root.querySelector('[data-save-note]');
    if (saveNote) saveNote.textContent = savedChartNote(saveOutcome);
    const marketingNote = root.querySelector('[data-marketing-note]');
    if (marketingNote) marketingNote.textContent = quizOutcomeSummary({ marketing: 'saved' }).marketing;
    renderSavedCharts(root);
    rememberQuizDismissal(365);
  } catch (error) {
    hideNote(root, 'status');
    setQuizOutcome(root, { preview: state.previewStatus || 'not_rendered', save: saveOutcome, marketing: 'failed' });
    const timedOut = error && error.name === 'AbortError';
    showError(
      root,
      'form',
      timedOut
        ? 'That took too long. Your details are still here — try again when the connection is steadier.'
        : 'We could not join the list just now. Your details are still here — try again in a moment.',
    );
  } finally {
    clearTimeout(reassure);
    clearTimeout(backstop);
    if (button) button.disabled = false;
  }
}

function buildMarketingOptInPayload({
  email,
  firstName,
  marketingOptIn,
  sunSign,
  date,
} = {}) {
  if (!marketingOptIn) return null;
  const tags = ['newsletter', 'find-your-sky', 'quiz-completed'];
  if (typeof sunSign === 'string' && sunSign.trim()) tags.push(`sun-${sunSign.trim().toLowerCase()}`);
  const birthMonthDay = toBirthMonthDay(date);
  if (birthMonthDay) tags.push(`birthday-${birthMonthDay}`);
  const payload = new URLSearchParams({
    form_type: 'customer',
    utf8: '✓',
    'contact[email]': typeof email === 'string' ? email.trim() : '',
    'contact[tags]': tags.join(','),
  });
  if (typeof firstName === 'string' && firstName.trim()) {
    payload.set('contact[first_name]', firstName.trim());
  }
  return payload;
}

async function submitMarketingOptIn({
  marketingOptIn = false,
  email,
  firstName,
  sunSign,
  date,
  signal,
  documentImpl = typeof document === 'undefined' ? null : document,
  submitImpl = submitShopifyCustomerForm,
} = {}) {
  if (!marketingOptIn) return { skipped: true };
  const payload = buildMarketingOptInPayload({ email, firstName, marketingOptIn, sunSign, date });
  return submitImpl({ payload, signal, documentImpl });
}

async function submitShopifyCustomerForm({ payload, signal, documentImpl } = {}) {
  if (!documentImpl?.body) throw new Error('document_unavailable');
  if (!(payload instanceof URLSearchParams)) throw new Error('payload_unavailable');

  const submissionId = `stz-quiz-marketing-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const frame = documentImpl.createElement('iframe');
  const form = documentImpl.createElement('form');

  frame.name = submissionId;
  frame.title = 'Email signup response';
  frame.tabIndex = -1;
  frame.style.display = 'none';
  form.method = 'post';
  form.action = MARKETING_OPT_IN_ENDPOINT;
  form.target = submissionId;
  form.acceptCharset = 'UTF-8';
  form.style.position = 'fixed';
  form.style.width = '1px';
  form.style.height = '1px';
  form.style.overflow = 'hidden';
  form.style.clipPath = 'inset(50%)';
  form.setAttribute('aria-hidden', 'true');

  for (const [name, value] of payload.entries()) {
    const input = documentImpl.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }
  documentImpl.body.append(frame, form);

  return new Promise((resolve, reject) => {
    let submitted = false;
    let settled = false;

    const cleanup = () => {
      signal?.removeEventListener('abort', onAbort);
      frame.removeEventListener('load', onLoad);
      form.remove();
      frame.remove();
    };
    const finish = (callback, value) => {
      if (settled) return;
      settled = true;
      cleanup();
      callback(value);
    };
    const onAbort = () => {
      const error = new Error('opt_in_aborted');
      error.name = 'AbortError';
      finish(reject, error);
    };
    const onLoad = () => {
      if (!submitted) return;
      try {
        const responseUrl = new URL(frame.contentWindow?.location.href || '', documentImpl.location?.href);
        if (responseUrl.searchParams.get('customer_posted') === 'true') {
          finish(resolve, { ok: true });
          return;
        }
        const responseDocument = frame.contentDocument;
        const responseText = responseDocument?.body?.textContent || '';
        const errorNode = responseDocument?.querySelector(
          '.errors, .form-status--error, [data-form-status="error"], .email-signup__message',
        );
        if (/missing captcha token/i.test(responseText) || errorNode) {
          finish(reject, new Error('opt_in_failed'));
        }
      } catch {
        // Shopify keeps the response same-origin. If an intermediary challenge is still resolving,
        // leave the frame alone and let the outer request backstop handle a genuine interruption.
      }
    };

    if (signal?.aborted) {
      onAbort();
      return;
    }
    signal?.addEventListener('abort', onAbort, { once: true });
    frame.addEventListener('load', onLoad);
    submitted = true;
    const captcha = documentImpl.defaultView?.Shopify?.captcha;
    if (captcha && typeof captcha.protect === 'function') {
      captcha.protect(form, () => form.submit());
    } else {
      form.submit();
    }
  });
}

function toBirthMonthDay(isoDate) {
  if (typeof isoDate !== 'string') return '';
  const match = /^\d{4}-(\d{2})-(\d{2})$/.exec(isoDate);
  return match ? `${match[1]}-${match[2]}` : '';
}

function renderSuccess(root, { optedIn, preview = 'not_rendered', save = 'skipped' }) {
  const title = root.querySelector('[data-success-title]');
  const body = root.querySelector('[data-success-body]');
  const previewNote = root.querySelector('[data-preview-note]');
  const saveNote = root.querySelector('[data-save-note]');
  const marketingNote = root.querySelector('[data-marketing-note]');
  if (optedIn) {
    if (title) title.textContent = 'You\u2019re in.';
    if (body) body.textContent = 'Your code is WELCOME10 for 10% off your first order. You are on the list for early access.';
  } else {
    if (title) title.textContent = 'Your preview is ready.';
    if (body) body.textContent = 'Shop your personalized piece whenever you are ready.';
  }
  if (previewNote) previewNote.textContent = quizOutcomeSummary({ preview }).preview;
  if (saveNote) saveNote.textContent = savedChartNote(save);
  if (marketingNote) marketingNote.textContent = optedIn
    ? quizOutcomeSummary({ marketing: 'saved' }).marketing
    : quizOutcomeSummary({ marketing: 'skipped' }).marketing;
  showStep(root, 'success');
}

function previewReadiness(chart = {}) {
  if (!String(chart.date || '').trim()) {
    return { ok: false, reason: 'missing_date', message: 'Add your birth date to see your wheel.' };
  }
  if (!String(chart.time || '').trim()) {
    return { ok: false, reason: 'missing_time', message: 'Add your birth time to see your wheel.', backTo: 'time', linkText: 'Add your birth time' };
  }
  const latText = String(chart.lat ?? '').trim();
  const lonText = String(chart.lon ?? '').trim();
  const lat = Number(latText);
  const lon = Number(lonText);
  if (!latText || !lonText || !Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) {
    return { ok: false, reason: 'missing_place', message: 'Choose your birthplace to see your wheel.', backTo: 'place', linkText: 'Choose your birthplace' };
  }
  return { ok: true, reason: 'ready' };
}

// The missing-input message carries its own way back: the phrase the founder read as a dead end
// ("Add your birth time") is the link to the step that takes it.
function renderPreviewStatus(status, readiness) {
  status.replaceChildren();
  if (!readiness.backTo || !readiness.linkText || !readiness.message.startsWith(readiness.linkText)) {
    status.textContent = readiness.message;
    return;
  }
  const link = document.createElement('button');
  link.type = 'button';
  link.className = 'stz-quiz__link';
  link.setAttribute('data-back', readiness.backTo);
  link.textContent = readiness.linkText;
  status.append(link, document.createTextNode(readiness.message.slice(readiness.linkText.length)));
}

function previewErrorMessage(error) {
  if (error?.kind === 'capability') return 'We could not start the chart preview. Please try again.';
  if (error?.kind === 'render') return 'We could not render that chart. Please try again.';
  if (error?.kind === 'malformed') return 'Preview returned an incomplete chart. Please try again.';
  if (error?.kind === 'timeout') return 'The chart preview took too long. Please try again.';
  return 'Preview is unavailable right now. Please try again.';
}

async function renderQuizPreview(root, state) {
  const status = root.querySelector('[data-quiz-preview-status]');
  const svgBox = root.querySelector('[data-quiz-preview-svg]');
  if (!status || !svgBox) return;

  const readiness = previewReadiness(state);
  svgBox.replaceChildren();
  if (!readiness.ok) {
    state.previewStatus = readiness.reason;
    renderPreviewStatus(status, readiness);
    return;
  }

  state.previewStatus = 'pending';
  status.textContent = 'Rendering your wheel…';
  try {
    const { requestChartPreview } = await import('./chart-preview-client.js');
    const result = await requestChartPreview({
      base: QUIZ_PREVIEW_ENDPOINT,
      template: 'wheel',
      chart: state,
    });
    svgBox.innerHTML = result.svg;
    state.previewStatus = 'rendered';
    status.textContent = 'Your wheel is ready.';
  } catch (error) {
    state.previewStatus = error?.kind || 'network';
    status.textContent = previewErrorMessage(error);
  }
}

function quizOutcomeSummary({ preview = 'not_rendered', save = 'skipped', marketing = 'skipped' } = {}) {
  const previewCopy = {
    rendered: 'Chart preview ready.',
    missing_date: 'Chart preview needs your birth date.',
    missing_time: 'Chart preview needs your birth time.',
    missing_place: 'Chart preview needs your birthplace.',
    capability: 'Chart preview could not start.',
    render: 'Chart preview could not render.',
    malformed: 'Chart preview returned an incomplete result.',
    timeout: 'Chart preview took too long. Please try again.',
    network: 'Chart preview was unavailable.',
    pending: 'Chart preview is still rendering.',
  }[preview] || 'Chart preview was not rendered.';
  const marketingCopy = marketing === 'saved'
    ? 'You joined the early-access list.'
    : marketing === 'failed'
      ? 'We could not join the list. Your preview and local save remain unchanged.'
      : 'You did not join the early-access list.';
  return {
    preview: previewCopy,
    save: savedChartNote(save),
    marketing: marketingCopy,
  };
}

function setQuizOutcome(root, outcomes) {
  const node = root.querySelector('[data-quiz-outcome]');
  if (!node) return;
  const summary = quizOutcomeSummary(outcomes);
  node.textContent = `${summary.preview} ${summary.save} ${summary.marketing}`;
  node.hidden = false;
}

function prefillPersonalizedProduct() {
  if (typeof document === 'undefined' || typeof location === 'undefined') return false;
  if (!location.pathname.startsWith('/products/')) return false;
  const handle = location.pathname.match(/\/products\/([^/?#]+)/)?.[1] || '';
  if (FIXED_ART_BUNDLE_HANDLES.has(handle)) return false;
  if (!document.querySelector(FIELD_SELECTOR.date)) return false;
  const chart = savedCharts().find((candidate) => candidate.isSelf === true);
  if (!chart) return false;
  applyChart(chart);
  return true;
}

// The single write path out of the quiz. Nothing else in this file persists a chart from the quiz
// flow, so "the customer did not tick the box" is one branch, in one place, that can be proved.
function persistQuizChart(chart, wantsSavedChart) {
  if (!wantsSavedChart) return 'skipped';
  return saveChart(chart) ? 'saved' : 'failed';
}

// Say what actually happened. A browser in private mode, or out of quota, silently refuses the
// write, and promising a saved chart there is a promise about the customer's own data that we know
// to be false.
function savedChartNote(outcome) {
  if (outcome === 'saved') {
    return 'Your birth details are saved in this browser for 30 days. Reopen this window to delete them sooner.';
  }
  if (outcome === 'failed') {
    return 'We could not save your birth details on this device, so nothing was stored here.';
  }
  return 'Nothing was stored on this device. You will add your birth details on the product page when you order.';
}

function renderReveal(root, sign) {
  const title = root.querySelector('[data-reveal-title]');
  const line = root.querySelector('[data-reveal-line]');
  if (title) title.textContent = `You are a ${sign} sun.`;
  if (line) line.textContent = SIGN_LINES[sign] || '';
}

async function searchPlace(input, suggestions, state) {
  state.lat = '';
  state.lon = '';
  const query = input.value.trim();
  if (!suggestions || query.length < 3) {
    closeSuggestions(suggestions);
    return;
  }
  try {
    // fetch DEEP, show few (see birth-place.js): counties dominate the shallow ranks for bare
    // same-named queries; a deep fetch + populated-first sort puts the real towns in view.
    const response = await fetch(`${PHOTON_ENDPOINT}?q=${encodeURIComponent(query)}&limit=${QUIZ_PLACE_FETCH_LIMIT}&lang=en&osm_tag=place`);
    if (!response.ok) return;
    const data = await response.json();
    // Same disambiguation as birth-place.js (the wrong-Warren bug): admin areas always type-tagged,
    // populated places sorted first, colliding labels gain their county, THEN dedupe — so two
    // different same-named places never fold into one row.
    const isPopulated = (f) => QUIZ_POPULATED_PLACE_TYPES.has(String((f.properties || {}).osm_value || '').replace(/_/g, ' '));
    const featuresOrdered = [...(data.features || []).filter(isPopulated), ...(data.features || []).filter((f) => !isPopulated(f))];
    const dupCounts = (list) => {
      const m = new Map();
      for (const item of list) m.set(item.label, (m.get(item.label) || 0) + 1);
      return m;
    };
    let places = featuresOrdered.map((feature) => ({ feature, label: placeLabel(feature) }));
    const counts = dupCounts(places);
    places = places.map((item) => (counts.get(item.label) > 1 ? { feature: item.feature, label: placeLabel(item.feature, { county: true }) } : item));
    const seen = new Set();
    places = places.filter((item) => {
      if (!item.label || seen.has(item.label)) return false;
      seen.add(item.label);
      return true;
    }).slice(0, QUIZ_PLACE_MAX_SUGGESTIONS);
    suggestions.innerHTML = places.map((item, index) => `<li><button type="button" data-place-index="${index}">${escapeHtml(item.label)}</button></li>`).join('');
    suggestions.hidden = places.length === 0;
    suggestions.querySelectorAll('[data-place-index]').forEach((button) => {
      button.addEventListener('mousedown', (event) => {
        event.preventDefault();
        const item = places[Number(button.getAttribute('data-place-index'))];
        const coords = item.feature?.geometry?.coordinates || [];
        input.value = item.label;
        state.place = item.label;
        state.lon = coords[0] != null ? String(coords[0]) : '';
        state.lat = coords[1] != null ? String(coords[1]) : '';
        closeSuggestions(suggestions);
      });
    });
  } catch {
    closeSuggestions(suggestions);
  }
}

async function resolveTypedPlace(query, state, signal) {
  try {
    const response = await fetch(`${PHOTON_ENDPOINT}?q=${encodeURIComponent(query)}&limit=1&lang=en`, { signal });
    if (!response.ok) return;
    const data = await response.json();
    const feature = data.features?.[0];
    const coords = feature?.geometry?.coordinates || [];
    const label = placeLabel(feature) || query;
    state.place = label;
    state.lon = coords[0] != null ? String(coords[0]) : '';
    state.lat = coords[1] != null ? String(coords[1]) : '';
    const input = document.querySelector('[data-quiz-place]');
    if (input && state.place) input.value = state.place;
  } catch (error) {
    // An abort means the whole attempt timed out and the caller has to hear about it. Everything
    // else stays tolerated: a typed place is still useful for the contact record, and product pages
    // ask for an exact place when coordinates are missing.
    if (error && error.name === 'AbortError') throw error;
  }
}

// the place types a birth certificate names, shown untagged; everything else carries its type
// (a county named "Warren" is indistinguishable from Warren the city without it)
const QUIZ_PLAIN_PLACE_TYPES = new Set(['city', 'town', 'village', 'hamlet']);
// populated places (sorted above counties/regions/districts in the suggestion list)
const QUIZ_POPULATED_PLACE_TYPES = new Set(['city', 'town', 'village', 'hamlet', 'suburb', 'quarter', 'neighbourhood', 'borough', 'locality']);
// fetch deep, show few: counties dominate the shallow ranks for bare same-named queries
const QUIZ_PLACE_FETCH_LIMIT = 30;
const QUIZ_PLACE_MAX_SUGGESTIONS = 8;

function placeLabel(feature, { county = false } = {}) {
  const props = feature?.properties || {};
  const name = props.name || props.city || '';
  const t = String(props.osm_value || '').replace(/_/g, ' ');
  const typed = name && t && !QUIZ_PLAIN_PLACE_TYPES.has(t) ? `${name} (${t})` : name;
  return [typed, county ? props.county : '', props.state, props.country].filter(Boolean).join(', ');
}

function closeSuggestions(suggestions) {
  if (!suggestions) return;
  suggestions.hidden = true;
  suggestions.innerHTML = '';
}

function optionalBirthTimeFields({ time, timeUnknown, timeNote } = {}) {
  const fields = {};
  if (!isClockTime(time) && timeUnknown === true) fields.timeUnknown = true;
  const note = typeof timeNote === 'string' ? timeNote.trim() : '';
  if (note) fields.timeNote = note;
  return fields;
}

function clockTimeParts(time) {
  if (!isClockTime(time)) return null;
  const hour24 = Number(time.slice(0, 2));
  const minute = Number(time.slice(3, 5));
  return {
    time,
    hour24,
    minute,
    ampm: hour24 >= 12 ? 'PM' : 'AM',
    hour12: hour24 % 12 === 0 ? 12 : hour24 % 12,
  };
}

function fillTimePicker(parts) {
  if (typeof document === 'undefined' || typeof document.querySelector !== 'function') return;
  const timeField = document.querySelector(FIELD_SELECTOR.time);
  const picker = (timeField && typeof timeField.closest === 'function' && timeField.closest('stz-time-picker'))
    || document.querySelector('stz-time-picker');
  if (!picker || typeof picker.querySelector !== 'function') return;
  const hourEl = picker.querySelector('[data-time-hour]');
  const minEl = picker.querySelector('[data-time-min]');
  const ampmBtns = typeof picker.querySelectorAll === 'function'
    ? picker.querySelectorAll('[data-time-ampm]')
    : [];
  if (parts) {
    if (hourEl) hourEl.value = String(parts.hour12);
    if (minEl) minEl.value = String(parts.minute);
    picker.ampm = parts.ampm;
    for (const btn of ampmBtns) {
      const value = btn.getAttribute?.('data-time-ampm') || btn.dataset?.timeAmpm;
      btn.classList?.toggle?.('is-active', value === parts.ampm);
    }
    return;
  }
  if (hourEl) hourEl.value = '';
  if (minEl) minEl.value = '';
  picker.ampm = '';
  for (const btn of ampmBtns) btn.classList?.remove?.('is-active');
}

function applyExactTimeRepresentation(time) {
  const parts = clockTimeParts(time);
  fillTimePicker(parts);
  if (typeof MutationObserver !== 'function' || typeof document === 'undefined') return;
  const timeField = document.querySelector?.(FIELD_SELECTOR.time);
  const picker = (timeField && typeof timeField.closest === 'function' && timeField.closest('stz-time-picker'))
    || document.querySelector?.('stz-time-picker');
  if (!picker || picker.dataset?.ready === 'true') return;
  const observer = new MutationObserver(() => {
    if (picker.dataset?.ready === 'true') {
      observer.disconnect();
      fillTimePicker(parts);
    }
  });
  try {
    observer.observe(picker, { attributes: true, attributeFilter: ['data-ready'] });
  } catch {
    observer.disconnect();
  }
}

function applyChart(chart = {}) {
  const time = isClockTime(chart.time) ? chart.time : '';
  setValue(FIELD_SELECTOR.date, chart.date || '');
  setValue(FIELD_SELECTOR.time, time);
  applyExactTimeRepresentation(time);
  setValue(FIELD_SELECTOR.place, chart.place || '');
  setValue(FIELD_SELECTOR.lat, chart.lat || '');
  setValue(FIELD_SELECTOR.lon, chart.lon || '');
  // Never infer unknown from a missing time. A note is restored only when the customer stored one.
  setValue(FIELD_SELECTOR.timeNote, typeof chart.timeNote === 'string' ? chart.timeNote : '');
}

function clearBirthFields() {
  Object.values(FIELD_SELECTOR).forEach((selector) => setValue(selector, ''));
}

function setValue(selector, value) {
  const field = document.querySelector(selector);
  if (!field) return;
  field.value = value;
  field.dispatchEvent(new Event('input', { bubbles: true }));
  field.dispatchEvent(new Event('change', { bubbles: true }));
}

function readProductBirthChart(label, isSelf) {
  const time = document.querySelector(FIELD_SELECTOR.time)?.value || '';
  const timeNote = document.querySelector(FIELD_SELECTOR.timeNote)?.value || '';
  return {
    id: isSelf ? 'me' : `chart-${Date.now()}`,
    label: label.trim() || (isSelf ? 'Me' : 'Saved chart'),
    isSelf,
    date: document.querySelector(FIELD_SELECTOR.date)?.value || '',
    time,
    place: document.querySelector(FIELD_SELECTOR.place)?.value || '',
    lat: document.querySelector(FIELD_SELECTOR.lat)?.value || '',
    lon: document.querySelector(FIELD_SELECTOR.lon)?.value || '',
    tz: '',
    ...optionalBirthTimeFields({ time, timeNote }),
  };
}

/* ---------------------------------------------------------------------------------------------
   Saved-chart storage. Everything below is the privacy contract, and it has two jobs at once:
   keep what is stored to the smallest honest thing, and never let storage itself become a way to
   break the store. Web Storage throws rather than returns on a blocked origin, a full quota, or
   Safari private mode, and those throws used to escape into the quiz submit and the add-to-cart
   path. Every accessor here is contained and returns a value; nothing in this section can throw
   into personalization, the cart, or checkout.
   ------------------------------------------------------------------------------------------- */

function storageGet(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key, value) {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function storageRemove(key) {
  try {
    localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

function sessionGet(key) {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function sessionSet(key, value) {
  try {
    sessionStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function dismissedUntilFrom(readLocal = storageGet) {
  const value = Number(readLocal(DISMISSED_UNTIL_KEY) || 0);
  return Number.isFinite(value) ? value : 0;
}

function quizAutoOpenStorageState({ readLocal = storageGet, readSession = sessionGet } = {}) {
  return {
    dismissedUntil: dismissedUntilFrom(readLocal),
    sessionShown: Boolean(readSession(AUTO_OPEN_SESSION_KEY)),
    manualSuppressed: Boolean(readSession(AUTO_OPEN_MANUAL_SUPPRESS_KEY)),
  };
}

function rememberQuizDismissal(days) {
  return storageSet(DISMISSED_UNTIL_KEY, String(Date.now() + days * 86400000));
}

function quizDismissedUntil() {
  return dismissedUntilFrom();
}

/**
 * A real date on the real calendar, inside the window the form allows. `/\d{4}-\d{2}-\d{2}/` is a
 * shape check, not a date check: it happily passes 9999-99-99 and 2023-02-29, and Date parsing
 * rolls those forward into a different, wrong day rather than refusing them. Building the day back
 * out of Date.UTC and demanding the same three numbers is what makes a non-day fail closed.
 */
function isCalendarDate(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  if (value < SAVED_CHART_MIN_DATE || value > today()) return false;
  const [year, month, day] = value.split('-').map(Number);
  const stamp = new Date(Date.UTC(year, month - 1, day));
  return stamp.getUTCFullYear() === year && stamp.getUTCMonth() === month - 1 && stamp.getUTCDate() === day;
}

// 24-hour clock time. The old shape check accepted 99:99, which is not a time and would have been
// handed to the engine as one.
function isClockTime(value) {
  return typeof value === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
}

function boundedText(value, max) {
  if (value == null) return '';
  if (typeof value !== 'string' || value.length > max) return null;
  return value;
}

// Coordinates come back from Photon as strings and go into line-item properties as strings, so they
// stay strings here. The shape check stops the field being used as a general text slot, and the
// limit is per axis: latitude past 90 is not a place on Earth, and one shared 180 ceiling let every
// impossible latitude through.
function boundedCoordinate(value, limit) {
  const text = boundedText(value, 32);
  if (text === null || text === '') return text;
  if (!/^-?\d{1,3}(\.\d{1,10})?$/.test(text)) return null;
  return Math.abs(Number(text)) <= limit ? text : null;
}

// Metadata is either absent - a legacy record, written before this contract existed, which migrates
// - or present and correct. It is never present and repaired: a record carrying a version we do not
// know, or a timestamp that is not a timestamp, is a record we cannot reason about, and guessing on
// its behalf is exactly the "accept anything, fix it up" behavior this contract replaced.
function readMetaNumber(value) {
  if (value === undefined) return { missing: true, value: null };
  if (typeof value !== 'number' || !Number.isFinite(value)) return { invalid: true, value: null };
  return { value };
}

/**
 * Turn one stored value into a chart we are willing to keep, or into nothing.
 *
 * Fail closed everywhere: an unknown key, a field of the wrong type or beyond its length, a
 * malformed date or time, a coordinate that is not one, or a deadline that has passed all reject
 * the whole record. Nothing is repaired and kept, because a record we had to repair is a record we
 * do not understand, and this key holds someone's birth data.
 */
function normalizeSavedChart(raw, now) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  for (const key of Object.keys(raw)) {
    if (!SAVED_CHART_KEYS.has(key)) return null;
  }
  if (typeof raw.id !== 'string' || !/^[A-Za-z0-9_-]{1,64}$/.test(raw.id)) return null;
  if (!isCalendarDate(raw.date)) return null;
  if (raw.isSelf != null && typeof raw.isSelf !== 'boolean') return null;
  // A schema version we did not write is not ours to interpret.
  if (raw.v !== undefined && raw.v !== SAVED_CHART_SCHEMA) return null;

  const time = raw.time == null ? '' : raw.time;
  if (time !== '' && !isClockTime(time)) return null;
  if (raw.timeUnknown != null && typeof raw.timeUnknown !== 'boolean') return null;
  const timeNote = raw.timeNote == null ? '' : boundedText(raw.timeNote, SAVED_CHART_MAX_FIELD_CHARS);
  if (timeNote === null) return null;

  const label = boundedText(raw.label, SAVED_CHART_MAX_FIELD_CHARS);
  const place = boundedText(raw.place, SAVED_CHART_MAX_FIELD_CHARS);
  const tz = boundedText(raw.tz, SAVED_CHART_MAX_TZ_CHARS);
  const lat = boundedCoordinate(raw.lat, 90);
  const lon = boundedCoordinate(raw.lon, 180);
  if (label === null || place === null || tz === null || lat === null || lon === null) return null;

  // The deadline is ours, not the record's. A record with NO expiry is legacy - written before the
  // policy existed - and gets exactly one bounded life starting now; a record claiming a longer life
  // than the policy allows is clamped to the policy; a record whose expiry is present but is not a
  // number is malformed and dies here rather than being handed a fresh 30 days as if it were
  // legacy. Either way nothing outlives 30 days, and an expired record is dropped before any caller
  // can read a value out of it.
  const ceiling = now + SAVED_CHART_TTL_MS;
  const claimed = readMetaNumber(raw.expiresAt);
  if (claimed.invalid) return null;
  const expiresAt = claimed.missing ? ceiling : Math.min(claimed.value, ceiling);
  if (expiresAt <= now) return null;

  // A save that has not happened yet did not happen. The expiry above is clamped independently, so
  // this only rejects records that contradict themselves.
  const claimedSavedAt = readMetaNumber(raw.savedAt);
  if (claimedSavedAt.invalid) return null;
  if (!claimedSavedAt.missing && claimedSavedAt.value > now) return null;
  const savedAt = claimedSavedAt.missing ? now : claimedSavedAt.value;

  const record = {
    v: SAVED_CHART_SCHEMA,
    id: raw.id,
    label: label || 'Saved chart',
    isSelf: raw.isSelf === true,
    date: raw.date,
    time,
    place,
    lat,
    lon,
    tz,
    savedAt,
    expiresAt,
  };
  Object.assign(record, optionalBirthTimeFields({
    time,
    timeUnknown: raw.timeUnknown,
    timeNote,
  }));
  return record;
}

/**
 * Read the key with the work bounded at every step: bytes before we parse, items before we
 * validate, fields before we keep them. `changed` reports whether what we are willing to keep
 * differs from what is on disk, which is what drives the prune/migrate write-back in savedCharts().
 */
function loadSavedCharts() {
  const now = Date.now();
  const raw = storageGet(SAVED_CHARTS_KEY);
  if (raw == null) return { charts: [], changed: false, present: false };
  if (typeof raw !== 'string' || raw.length > SAVED_CHART_MAX_BYTES) {
    return { charts: [], changed: true, present: true };
  }
  let parsed = null;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { charts: [], changed: true, present: true };
  }
  if (!Array.isArray(parsed)) return { charts: [], changed: true, present: true };

  const charts = [];
  const seen = new Set();
  for (const item of parsed.slice(0, SAVED_CHART_MAX_ITEMS)) {
    const chart = normalizeSavedChart(item, now);
    if (!chart || seen.has(chart.id)) continue;
    seen.add(chart.id);
    charts.push(chart);
  }
  const changed = charts.length !== parsed.length
    || charts.some((chart, index) => JSON.stringify(chart) !== JSON.stringify(parsed[index]));
  return { charts, changed, present: true };
}

/**
 * The only read. It prunes before it returns: expired, malformed, oversized and excess records are
 * taken out of storage BEFORE any value can be applied to a form, and a valid legacy record is
 * written back once carrying the bounded 30-day deadline.
 *
 * That write-back is not decoration, so it is not best effort either. A legacy record carries no
 * deadline, so the deadline is invented on read - and an invented deadline that never reaches the
 * device is not a deadline, it is a number this page load happened to think of. Hand the record
 * back anyway and the next read invents a later one from a later `now`, so the thirty days that
 * just elapsed never apply and the chart lives forever in thirty-day steps. Read-success with
 * write-denial is an ordinary browser state - full quota, private mode, blocked storage - not an
 * exotic one. So a list that differs from what is on disk becomes usable only once it IS on disk;
 * if the write is refused we take the key out instead and return nothing. Removal is the part that
 * stays best effort: a browser may refuse that too, and the bytes may survive, but they survive
 * unusable, because every read runs this same gate. Callers get an empty list and carry on - the
 * quiz, the cart and the checkout never see a failure here.
 */
function savedCharts() {
  const { charts, changed, present } = loadSavedCharts();
  if (!changed) return charts;
  if (!charts.length) {
    if (present) storageRemove(SAVED_CHARTS_KEY);
    return charts;
  }
  if (storageSet(SAVED_CHARTS_KEY, JSON.stringify(charts))) return charts;
  storageRemove(SAVED_CHARTS_KEY);
  return [];
}

/**
 * The only write. The candidate is validated by exactly the same rules as anything already on disk,
 * with a fresh 30-day deadline, so a caller cannot store a shape a read would reject. Returns
 * whether the chart is genuinely on the device: false for a rejected record and false for a browser
 * that refused the write, and callers tell the customer which happened.
 */
function saveChart(chart) {
  const now = Date.now();
  // Metadata belongs to the contract, not the caller. A candidate that already carries a version or
  // timestamps is not a fresh chart, and stamping our own over the top would be the same silent
  // repair the read path refuses - it would let a bad value in by overwriting it instead of
  // noticing it. Callers hand over chart fields; the deadline is ours to set.
  if (chart && typeof chart === 'object'
    && (chart.v !== undefined || chart.savedAt !== undefined || chart.expiresAt !== undefined)) {
    return false;
  }
  const record = normalizeSavedChart(
    { ...chart, v: SAVED_CHART_SCHEMA, savedAt: now, expiresAt: now + SAVED_CHART_TTL_MS },
    now,
  );
  if (!record) return false;
  const next = [record, ...savedCharts().filter((item) => item.id !== record.id)]
    .slice(0, SAVED_CHART_MAX_ITEMS);
  return storageSet(SAVED_CHARTS_KEY, JSON.stringify(next));
}

function deleteSavedChart(id) {
  const remaining = savedCharts().filter((chart) => chart.id !== id);
  if (!remaining.length) return storageRemove(SAVED_CHARTS_KEY);
  return storageSet(SAVED_CHARTS_KEY, JSON.stringify(remaining));
}

function clearSavedCharts() {
  return storageRemove(SAVED_CHARTS_KEY);
}

function sunSign(dateValue) {
  const [, monthRaw, dayRaw] = String(dateValue || '').split('-').map(Number);
  const value = monthRaw * 100 + dayRaw;
  if (value >= 321 && value <= 419) return 'Aries';
  if (value >= 420 && value <= 520) return 'Taurus';
  if (value >= 521 && value <= 620) return 'Gemini';
  if (value >= 621 && value <= 722) return 'Cancer';
  if (value >= 723 && value <= 822) return 'Leo';
  if (value >= 823 && value <= 922) return 'Virgo';
  if (value >= 923 && value <= 1022) return 'Libra';
  if (value >= 1023 && value <= 1121) return 'Scorpio';
  if (value >= 1122 && value <= 1221) return 'Sagittarius';
  if (value >= 1222 || value <= 119) return 'Capricorn';
  if (value >= 120 && value <= 218) return 'Aquarius';
  return 'Pisces';
}

function showError(root, key, message) {
  const error = root.querySelector(`[data-error="${key}"]`);
  if (!error) return;
  error.textContent = message;
  error.hidden = false;
}

// The status line is role="status" aria-live="polite": it appears without moving focus, which is
// exactly the case WCAG 2.2 SC 4.1.3 covers, so it has to be announced rather than merely visible.
function showStatus(root, message) {
  const status = root.querySelector('[data-status]');
  if (!status) return;
  status.textContent = message;
  status.hidden = false;
}

function hideNote(root, key) {
  const node = key === 'status'
    ? root.querySelector('[data-status]')
    : root.querySelector(`[data-error="${key}"]`);
  if (!node) return;
  node.textContent = '';
  node.hidden = true;
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function debounce(fn, wait) {
  let timer = 0;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
}

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  })[char]);
}

// Functions only, never data. The storage contract above is a privacy boundary, and a boundary that
// cannot be exercised without a browser cannot be proved; scripts/tests/saved-chart-privacy.test.mjs
// loads this asset with no DOM and drives exactly these entry points, and contained browser QA can
// use the same seam to check deletion.
globalThis.StarlyzedSavedCharts = {
  SAVED_CHARTS_KEY,
  SAVED_CHART_TTL_MS,
  SAVED_CHART_MAX_ITEMS,
  SAVED_CHART_MAX_BYTES,
  MARKETING_OPT_IN_ENDPOINT,
  savedCharts,
  saveChart,
  deleteSavedChart,
  clearSavedCharts,
  persistQuizChart,
  savedChartNote,
  birthDateLabel,
  renderSavedCharts,
  prefillPersonalizedProduct,
  applyChart,
  rememberQuizDismissal,
  quizDismissedUntil,
  sessionGet,
  sessionSet,
  toBirthMonthDay,
};

globalThis.StarlyzedChartQuiz = {
  QUIZ_STORAGE_KEYS,
  AUTO_OPEN_MIN_DELAY_MS,
  AUTO_OPEN_MAX_DELAY_MS,
  AUTO_OPEN_SCROLL_RATIO,
  AUTO_OPEN_SCROLL_PX,
  previewReadiness,
  quizOutcomeSummary,
  prefillPersonalizedProduct,
  renderQuizPreview,
  buildMarketingOptInPayload,
  submitMarketingOptIn,
  quizAutoOpenEligible,
  quizAutoOpenStorageState,
  quizAutoOpenScrollReached,
  createQuizAutoOpenController,
  createQuizRevealController,
  bindQuizLauncherDismissal,
  dismissQuizLauncher,
  hideQuizLauncher,
  createQuizLauncherCollisionController,
  launcherCollisionIntersections,
  applyChart,
  optionalBirthTimeFields,
  clockTimeParts,
};
